import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { RichTextEditor } from "../components/shared/RichTextEditor";
import { useForm, Controller } from "react-hook-form";
import { ImageUpload } from "../../../components/common/ImageUpload";
import { ArrowLeft, Save, Search, FileText, Clock, Globe } from "lucide-react";
import { useAdminT } from "../../../hooks/useAdminT";
import clsx from "clsx";

// Character counter bar
function CharCount({ value, max }: { value: string; max: number }) {
    const len = (value || '').length;
    const pct = Math.min((len / max) * 100, 100);
    const color = len > max ? 'bg-red-500' : len > max * 0.85 ? 'bg-amber-400' : 'bg-emerald-500';
    return (
        <div className="mt-1">
            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className={clsx("h-full rounded-full transition-all", color)} style={{ width: `${pct}%` }} />
            </div>
            <p className={clsx("text-xs mt-1 text-right", len > max ? 'text-red-500' : 'text-slate-400')}>
                {len}/{max}
            </p>
        </div>
    );
}

// Google SERP preview card
function SerpPreview({ url, title, description }: { url: string; title: string; description: string }) {
    return (
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-1 font-[Arial,sans-serif]">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Search className="w-3 h-3" /> Google Preview
            </p>
            <div className="text-xs text-green-700 truncate">{url || 'https://yoursite.com/blog/...'}</div>
            <div className="text-blue-700 text-base font-medium truncate hover:underline cursor-pointer leading-snug">
                {title || 'Post Title Here'}
            </div>
            <div className="text-slate-600 text-sm leading-snug line-clamp-2">
                {description || 'Your meta description will appear here as a summary on Google search results.'}
            </div>
        </div>
    );
}

// Auto-generate slug from English title
function slugify(text: string): string {
    return text.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 80);
}

// Read time estimate
function estimateReadTime(html: string): number {
    const text = (html || '').replace(/<[^>]+>/g, ' ');
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
}

type TabId = 'content' | 'seo';

export default function BlogFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useAdminT();
    const queryClient = useQueryClient();
    const isEdit = !!id;
    const [tab, setTab] = useState<TabId>('content');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

    const { register, handleSubmit, reset, control, setValue, watch } = useForm({
        defaultValues: {
            slug: '',
            coverImage: '',
            published: false,
            publishedAt: '',
            categoryId: '',
            titleEn: '',
            titleAr: '',
            contentEn: '',
            contentAr: '',
            excerptEn: '',
            excerptAr: '',
            metaTitleEn: '',
            metaTitleAr: '',
            metaDescriptionEn: '',
            metaDescriptionAr: '',
        }
    });

    const watchedTitleEn = watch('titleEn');
    const watchedContentEn = watch('contentEn');
    const watchedMetaTitleEn = watch('metaTitleEn');
    const watchedMetaDescEn = watch('metaDescriptionEn');

    // Auto-slug from EN title
    useEffect(() => {
        if (!slugManuallyEdited && watchedTitleEn) {
            setValue('slug', slugify(watchedTitleEn));
        }
    }, [watchedTitleEn, slugManuallyEdited, setValue]);

    const readTime = estimateReadTime(watchedContentEn);

    // Fetch categories
    const { data: categories = [] } = useQuery<any[]>({
        queryKey: ['blog', 'categories'],
        queryFn: () => api.get('/api/admin/blog/categories?locale=en').then(r => r.data.data || []),
    });

    // Fetch post for edit
    const { data: post } = useQuery({
        queryKey: ['blog', id],
        queryFn: () => api.get(`/api/admin/blog/${id}`).then(res => res.data.data),
        enabled: isEdit,
    });

    useEffect(() => {
        if (post) {
            const enT = post.translations?.find((t: any) => t.locale === 'en') || {};
            const arT = post.translations?.find((t: any) => t.locale === 'ar') || {};
            setSlugManuallyEdited(true); // Don't auto-overwrite slug on edit
            reset({
                slug: post.slug || '',
                coverImage: post.coverImage || '',
                published: post.published ?? false,
                publishedAt: post.publishedAt ? new Date(post.publishedAt).toISOString().slice(0, 16) : '',
                categoryId: post.categoryId || '',
                titleEn: enT.title || '',
                titleAr: arT.title || '',
                contentEn: enT.content || '',
                contentAr: arT.content || '',
                excerptEn: enT.excerpt || '',
                excerptAr: arT.excerpt || '',
                metaTitleEn: enT.metaTitle || '',
                metaTitleAr: arT.metaTitle || '',
                metaDescriptionEn: enT.metaDescription || '',
                metaDescriptionAr: arT.metaDescription || '',
            });
        }
    }, [post, reset]);

    const mutation = useMutation({
        mutationFn: (data: any) =>
            isEdit ? api.put(`/api/admin/blog/${id}`, data) : api.post('/api/admin/blog', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['blog'] });
            navigate('/admin/blog');
        },
    });

    const onSubmit = (data: any) => {
        mutation.mutate({
            slug: data.slug,
            coverImage: data.coverImage || null,
            published: data.published,
            publishedAt: data.published ? (data.publishedAt ? new Date(data.publishedAt).toISOString() : new Date().toISOString()) : null,
            categoryId: data.categoryId || null,
            translations: [
                {
                    locale: 'en',
                    title: data.titleEn,
                    content: data.contentEn || '',
                    excerpt: data.excerptEn || null,
                    metaTitle: data.metaTitleEn || null,
                    metaDescription: data.metaDescriptionEn || null,
                },
                {
                    locale: 'ar',
                    title: data.titleAr || data.titleEn,
                    content: data.contentAr || '',
                    excerpt: data.excerptAr || null,
                    metaTitle: data.metaTitleAr || null,
                    metaDescription: data.metaDescriptionAr || null,
                },
            ],
        });
    };

    const inputClass = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition";
    const labelClass = "block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1";

    const tabs: { id: TabId; label: string; icon: any }[] = [
        { id: 'content', label: 'Content', icon: FileText },
        { id: 'seo', label: 'SEO & Meta', icon: Globe },
    ];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Top bar */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/admin/blog')} className="p-2 hover:bg-slate-100 rounded-lg transition">
                        <ArrowLeft className="w-5 h-5 text-slate-600" />
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-slate-900">{isEdit ? t('blog.edit') : t('blog.add')}</h1>
                        {readTime > 0 && watchedContentEn && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {readTime} min read
                            </span>
                        )}
                    </div>
                </div>
                <button
                    onClick={handleSubmit(onSubmit)}
                    disabled={mutation.isPending}
                    className="flex items-center gap-2 px-5 py-2 bg-secondary text-white rounded-lg hover:bg-primary transition disabled:opacity-50 text-sm font-medium"
                >
                    <Save className="w-4 h-4" />
                    {mutation.isPending ? t('common.saving') : isEdit ? t('blog.update') : t('blog.save')}
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex gap-8">
                    {/* Main content area */}
                    <div className="flex-1 min-w-0">
                        {/* Tabs */}
                        <div className="flex gap-1 mb-6 bg-white rounded-xl border border-slate-200 p-1 w-fit">
                            {tabs.map(({ id: tabId, label, icon: Icon }) => (
                                <button
                                    key={tabId}
                                    type="button"
                                    onClick={() => setTab(tabId)}
                                    className={clsx(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition",
                                        tab === tabId ? "bg-secondary text-white" : "text-slate-600 hover:bg-slate-100"
                                    )}
                                >
                                    <Icon className="w-4 h-4" />
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Content Tab */}
                        {tab === 'content' && (
                            <div className="space-y-6">
                                {/* English section */}
                                <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <h2 className="font-semibold text-slate-800">English Content</h2>
                                    </div>
                                    <div>
                                        <label className={labelClass}>{t('blog.fields.titleEn')}</label>
                                        <input {...register('titleEn', { required: true })} className={inputClass} placeholder="Enter the post title in English..." />
                                    </div>
                                    <div>
                                        <label className={labelClass}>{t('blog.fields.excerptEn')}</label>
                                        <textarea
                                            {...register('excerptEn')}
                                            rows={2}
                                            className={inputClass}
                                            placeholder={t('blog.placeholders.excerptEn')}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>{t('blog.fields.contentEn')}</label>
                                        <Controller
                                            name="contentEn"
                                            control={control}
                                            render={({ field }) => (
                                                <RichTextEditor
                                                    value={field.value || ''}
                                                    onChange={field.onChange}
                                                    placeholder={t('blog.placeholders.contentEn')}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Arabic section */}
                                <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5" dir="rtl">
                                    <div className="flex items-center gap-2 mb-1" dir="ltr">
                                        <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                                        <h2 className="font-semibold text-slate-800">المحتوى العربي</h2>
                                    </div>
                                    <div>
                                        <label className={labelClass}>{t('blog.fields.titleAr')}</label>
                                        <input {...register('titleAr')} className={inputClass} placeholder="عنوان المقال بالعربية..." />
                                    </div>
                                    <div>
                                        <label className={labelClass}>{t('blog.fields.excerptAr')}</label>
                                        <textarea
                                            {...register('excerptAr')}
                                            rows={2}
                                            className={inputClass}
                                            placeholder={t('blog.placeholders.excerptAr')}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>{t('blog.fields.contentAr')}</label>
                                        <Controller
                                            name="contentAr"
                                            control={control}
                                            render={({ field }) => (
                                                <RichTextEditor
                                                    value={field.value || ''}
                                                    onChange={field.onChange}
                                                    placeholder={t('blog.placeholders.contentAr')}
                                                    dir="rtl"
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SEO Tab */}
                        {tab === 'seo' && (
                            <div className="space-y-6">
                                {/* English SEO */}
                                <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <h2 className="font-semibold text-slate-800">SEO — English</h2>
                                    </div>
                                    <div>
                                        <label className={labelClass}>Meta Title (EN) — max 60 chars</label>
                                        <input
                                            {...register('metaTitleEn')}
                                            className={inputClass}
                                            placeholder="SEO title for search engines..."
                                        />
                                        <CharCount value={watchedMetaTitleEn} max={60} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Meta Description (EN) — max 160 chars</label>
                                        <textarea
                                            {...register('metaDescriptionEn')}
                                            rows={3}
                                            className={inputClass}
                                            placeholder="Short summary for search engine results..."
                                        />
                                        <CharCount value={watchedMetaDescEn} max={160} />
                                    </div>
                                    <SerpPreview
                                        url={`https://hesnd.com/blog/${watch('slug') || '...'}`}
                                        title={watchedMetaTitleEn || watch('titleEn') || ''}
                                        description={watchedMetaDescEn || watch('excerptEn') || ''}
                                    />
                                </div>

                                {/* Arabic SEO */}
                                <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5" dir="rtl">
                                    <div className="flex items-center gap-2 mb-1" dir="ltr">
                                        <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                                        <h2 className="font-semibold text-slate-800">SEO — العربية</h2>
                                    </div>
                                    <div>
                                        <label className={labelClass}>عنوان ميتا (AR) — 60 حرف كحد أقصى</label>
                                        <input
                                            {...register('metaTitleAr')}
                                            className={inputClass}
                                            placeholder="عنوان لمحركات البحث..."
                                        />
                                        <CharCount value={watch('metaTitleAr')} max={60} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>الوصف التعريفي (AR) — 160 حرف كحد أقصى</label>
                                        <textarea
                                            {...register('metaDescriptionAr')}
                                            rows={3}
                                            className={inputClass}
                                            placeholder="ملخص قصير لنتائج محركات البحث..."
                                        />
                                        <CharCount value={watch('metaDescriptionAr')} max={160} />
                                    </div>
                                    <div dir="ltr">
                                        <SerpPreview
                                            url={`https://hesnd.com/ar/blog/${watch('slug') || '...'}`}
                                            title={watch('metaTitleAr') || watch('titleAr') || ''}
                                            description={watch('metaDescriptionAr') || watch('excerptAr') || ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sticky Sidebar */}
                    <div className="w-72 shrink-0">
                        <div className="sticky top-20 space-y-4">
                            {/* Publish box */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
                                <h3 className="font-semibold text-slate-800 text-sm">Publish Settings</h3>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="relative">
                                        <input type="checkbox" {...register('published')} className="sr-only peer" />
                                        <div className="w-10 h-5 bg-slate-200 peer-checked:bg-secondary rounded-full transition-colors" />
                                        <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                                    </div>
                                    <span className="text-sm font-medium text-slate-700">{t('common.published')}</span>
                                </label>

                                <div>
                                    <label className={labelClass}>Publish Date</label>
                                    <input type="datetime-local" {...register('publishedAt')} className={inputClass} />
                                </div>
                            </div>

                            {/* Slug box */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                                <h3 className="font-semibold text-slate-800 text-sm">{t('common.slug')}</h3>
                                <input
                                    {...register('slug', { required: true })}
                                    className={inputClass}
                                    placeholder="post-url-slug"
                                    onChange={(e) => {
                                        setSlugManuallyEdited(true);
                                        setValue('slug', e.target.value);
                                    }}
                                />
                                <p className="text-xs text-slate-400">Auto-generated from title. Edit to customize.</p>
                            </div>

                            {/* Category box */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                                <h3 className="font-semibold text-slate-800 text-sm">Category</h3>
                                <select {...register('categoryId')} className={inputClass}>
                                    <option value="">— No Category —</option>
                                    {(categories as any[]).map((cat: any) => (
                                        <option key={cat.id} value={cat.id}>{cat.name || cat.slug}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Cover Image */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                                <h3 className="font-semibold text-slate-800 text-sm">{t('common.image')}</h3>
                                <Controller
                                    name="coverImage"
                                    control={control}
                                    render={({ field }) => (
                                        <ImageUpload value={field.value} onChange={field.onChange} />
                                    )}
                                />
                            </div>

                            {/* Save button */}
                            <button
                                type="submit"
                                disabled={mutation.isPending}
                                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-secondary text-white rounded-xl hover:bg-primary transition disabled:opacity-50 font-medium"
                            >
                                <Save className="w-4 h-4" />
                                {mutation.isPending ? t('common.saving') : isEdit ? t('blog.update') : t('blog.save')}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/blog')}
                                className="w-full py-2.5 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
